import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Switch, Typography, message, Spin, Tabs, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Pie, PieChart } from 'recharts';
import axiosInstance from '../../utils/axiosInstance';

const { TabPane } = Tabs;

const COLORS = ['#465892', '#546DA6', '#BBC1C6', '#968EAF', '#6D5593', '#543D7B'];

const TeamStats = ({ gameId }) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [derivedTeamStats, setDerivedTeamStats] = useState({});
  const [analyticalTeamStats, setAnalyticalTeamStats] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch available teams for the game
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await axiosInstance.get(`/api/games/${gameId}/der_team_stats/`);
        const uniqueTeams = [...new Set(response.data.map((stat) => stat.team))];
        setTeams(uniqueTeams);
        if (uniqueTeams.length > 0) {
          setSelectedTeam(uniqueTeams[0]); // Default to the first team
        }
      } catch (error) {
        message.error('Failed to fetch teams');
      }
    };
    fetchTeams();
  }, [gameId]);

  // Fetch team stats based on selected team
  useEffect(() => {
    if (selectedTeam) {
      setLoading(true);
      const fetchTeamStats = async () => {
        try {
          const [derivedResponse, analyticalResponse] = await Promise.all([
            axiosInstance.get(`/api/games/${gameId}/der_team_stats/`),
            axiosInstance.get(`/api/games/${gameId}/ana_team_stats/`),
          ]);
          const derivedStats = derivedResponse.data.find((stat) => stat.team === selectedTeam);
          const analyticalStats = analyticalResponse.data.find((stat) => stat.team === selectedTeam);

          setDerivedTeamStats(derivedStats || {});
          setAnalyticalTeamStats(analyticalStats || {});
        } catch (error) {
          message.error('Failed to fetch team stats');
        } finally {
          setLoading(false);
        }
      };
      fetchTeamStats();
    }
  }, [selectedTeam, gameId]);

  const handleExport = () => {
    const data = [
      {
        Team: selectedTeam,
        Points: derivedTeamStats?.total_pts || 0,
        Assists: derivedTeamStats?.ast || 0,
        Rebounds: derivedTeamStats?.reb || 0,
        Turnovers: derivedTeamStats?.tov || 0,
        OffensiveRating: derivedTeamStats?.ortg || 'N/A',
        DefensiveRating: derivedTeamStats?.drtg || 'N/A',
        NetRating: derivedTeamStats?.net_rating || 'N/A',
        eFGPercentage: derivedTeamStats?.efg_pct || 'N/A',
        TSPercentage: derivedTeamStats?.ts_pct || 'N/A',
        OREB: derivedTeamStats?.oreb || 0,
        DREB: derivedTeamStats?.dreb || 0,
        FGA: derivedTeamStats?.fga || 0,
        FGM: derivedTeamStats?.fgm || 0,
        ThreePA: derivedTeamStats?.three_pa || 0,
        ThreePM: derivedTeamStats?.three_pm || 0,
        FTA: derivedTeamStats?.fta || 0,
        FTM: derivedTeamStats?.ftm || 0,
        TurnoverPercentage: analyticalTeamStats?.tov_pct || 'N/A',
        OffensiveEfficiency: derivedTeamStats?.oe || 'N/A',
        Pace: analyticalTeamStats?.pace || 'N/A',
        Possessions: derivedTeamStats?.team_poss || 'N/A',
        PointsPerPossession: derivedTeamStats?.pts_poss || 'N/A',
        EPSPerPossession: derivedTeamStats?.eps_poss || 'N/A',
        EOPPerPossession: derivedTeamStats?.eop_poss || 'N/A',
        FourFactors: analyticalTeamStats?.four_factors || 'N/A',
        OREBPercentage: analyticalTeamStats?.oreb_pct || 'N/A',
        DREBPercentage: analyticalTeamStats?.dreb_pct || 'N/A',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team Stats');
    XLSX.writeFile(wb, `team_stats_game_${gameId}_${selectedTeam}.xlsx`);
  };


  return (
    <div style={{ padding: '20px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '20px' }}>
        {/* Slider to select team */}
        <Col>
          <Switch
            checked={selectedTeam === teams[0]}
            onChange={(checked) => setSelectedTeam(checked ? teams[0] : teams[1])}
            checkedChildren={
              <Typography.Text style={{ fontSize: '1rem', color: '#fff', fontWeight: 'bold' }}>
                {teams[0] || 'Home'}
              </Typography.Text>
            }
            unCheckedChildren={
              <Typography.Text style={{ fontSize: '1rem', color: '#fff', fontWeight: 'bold' }}>
                {teams[1] || 'Away'}
              </Typography.Text>
            }
          />
        </Col>
        {/* Download Button */}
        <Col>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
          </Button>
        </Col>
      </Row>

      {loading ? (
        // Display a centered loading spinner while loading
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Banner */}
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col span={6}>
            <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Points
                        </Typography.Text>
                    }
                    style={{ textAlign: 'center' }}>
                <Typography.Text strong style={{ fontSize: '1.5rem' }}>
                  {derivedTeamStats?.total_pts || 'N/A'}
                </Typography.Text>
              </Card>
            </Col>
            <Col span={6}>
            <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Assists
                        </Typography.Text>
                    }
                    style={{ textAlign: 'center' }}>
                <Typography.Text strong style={{ fontSize: '1.5rem' }}>
                  {derivedTeamStats?.ast || 'N/A'}
                </Typography.Text>
              </Card>
            </Col>
            <Col span={6}>
            <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Rebounds
                        </Typography.Text>
                    }
                    style={{ textAlign: 'center' }}>
                <Typography.Text strong style={{ fontSize: '1.5rem' }}>
                  {derivedTeamStats?.reb || 'N/A'}
                </Typography.Text>
              </Card>
            </Col>
            <Col span={6}>
            <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Turnovers
                        </Typography.Text>
                    }
                    style={{ textAlign: 'center' }}>
                <Typography.Text strong style={{ fontSize: '1.5rem' }}>
                  {derivedTeamStats?.tov || 'N/A'}
                </Typography.Text>
              </Card>
            </Col>
          </Row>

          {/* Tabs for grouped stats */}
          <Tabs defaultActiveKey="1">
            {/* General Stats */}
            <TabPane tab="General Stats" key="1">

            {/* General Stats Chart */}
            <Row gutter={[16, 16]} justify="center">
                <Col span={24}>
                <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            General Stats
                        </Typography.Text>
                    }
                    style={{
                        backgroundColor: '#f5f5f5', // Light grey background>
                    }}>
                    <BarChart
                        width={1100}
                        height={300}
                        data={[
                        {
                            name: 'PTS',
                            value: derivedTeamStats?.total_pts || 0,
                        },
                        {
                            name: 'OPTS',
                            value: derivedTeamStats?.opp_pts || 0,
                        },
                        {
                            name: 'OFFRTG',
                            value: derivedTeamStats?.ortg ? parseFloat(derivedTeamStats.ortg.toFixed(2)) : 0,
                        },
                        {
                            name: 'DEFRTG',
                            value: derivedTeamStats?.drtg ? parseFloat(derivedTeamStats.drtg.toFixed(2)) : 0,
                        },
                        {
                            name: 'NETRTG',
                            value: derivedTeamStats?.net_rating ? parseFloat(derivedTeamStats.net_rating.toFixed(2)) : 0,
                        },
                        ]}
                    >
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value">
                        <Cell fill={COLORS[0]} />
                        <Cell fill={COLORS[3]} />
                        <Cell fill={COLORS[1]} />
                        <Cell fill={COLORS[4]} />
                        <Cell fill={COLORS[2]} />
                        </Bar>
                    </BarChart>
                    </Card>
                </Col>
                </Row>
            </TabPane>

            <TabPane tab="Efficiency & Four Factors" key="2">
                <Row gutter={[16, 16]}>
                    {/* Efficiency Metrics */}
                    <Col span={12}>
                    <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Efficiency Metrics
                        </Typography.Text>
                    }
                    style={{
                        backgroundColor: '#f5f5f5', // Light grey background>
                    }}>
                        {/* Offensive Efficiency */}
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: COLORS[6] }}>
                            Offensive Efficiency (OE):{' '}
                            {derivedTeamStats?.oe?.toFixed(2) || 'N/A'}
                        </Typography.Text>
                        </div>
                        {/* EPS and EOP Bar Chart */}
                        <BarChart
                        width={500}
                        height={250}
                        data={[
                            { name: 'EPS', value: derivedTeamStats?.eps || 0 },
                            { name: 'EOP', value: derivedTeamStats?.eop || 0 },
                        ]}
                        >
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => value.toFixed(2)} />
                        <Bar dataKey="value">
                            <Cell fill={COLORS[0]} />
                            <Cell fill={COLORS[3]} />
                        </Bar>
                        </BarChart>
                    </Card>
                    </Col>

                    {/* Four Factors Radar Chart */}
                    <Col span={12}>
                    <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Four Factors
                        </Typography.Text>
                    }>
                        <RadarChart
                        outerRadius={130}
                        width={500}
                        height={300}
                        data={[
                            { name: '4F', value: analyticalTeamStats?.four_factors || 0 },
                            { name: 'eFG%', value: derivedTeamStats?.efg_pct || 0 },
                            { name: 'OREB%', value: analyticalTeamStats?.oreb_pct || 0 },
                            { name: 'TOV%', value: analyticalTeamStats?.tov_pct || 0 },
                            { name: 'FTF', value: derivedTeamStats?.ft_factor || 0 },

                        ]}
                        >
                        <PolarGrid />
                        <PolarAngleAxis dataKey="name" />
                        <PolarRadiusAxis tickFormatter={(value) => `${(value).toFixed(2)}`} />
                        <Radar
                            dataKey="value"
                            stroke={COLORS[6]}
                            fill={COLORS[6]}
                            fillOpacity={0.6}
                        />
                        <Tooltip formatter={(value) => `${(value).toFixed(2)}`} />
                        </RadarChart>
                    </Card>
                    </Col>
                </Row>
                </TabPane>

                <TabPane tab="Pace & Possessions" key="3">
                    <Row gutter={[16, 16]}>
                        {/* Left Column: Pace and Possessions */}
                    <Col span={12}>
                    <Card
                        style={{
                        textAlign: 'center',
                        height: '400px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#f0f5ff',
                        }}
                    >
                        <Typography.Text
                        strong
                        style={{
                            fontSize: '1.3rem',
                            display: 'block',
                            marginBottom: '20px',
                            color: COLORS[3],
                        }}
                        >
                        <span style={{ fontWeight: 'bold' }}>Pace:</span> {analyticalTeamStats?.pace?.toFixed(2) || 'N/A'}
                        </Typography.Text>
                        <Typography.Text
                        strong
                        style={{
                            fontSize: '1.3rem',
                            display: 'block',
                            color: COLORS[3],
                        }}
                        >
                        <span style={{ fontWeight: 'bold' }}>Possessions:</span> {derivedTeamStats?.team_poss?.toFixed(2) || 'N/A'}
                        </Typography.Text>
                    </Card>
                    </Col>

                        {/* Right Column: Radar Chart */}
                        <Col span={12}>
                        <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Metrics per Possession
                        </Typography.Text>
                    }>
                            <RadarChart
                            outerRadius={130}
                            width={500}
                            height={300}
                            data={[
                                { name: 'PTS/POSS', value: derivedTeamStats?.pts_poss || 0 },
                                { name: 'EOP/POSS', value: derivedTeamStats?.eop_poss || 0 },
                                { name: 'EPS/POSS', value: derivedTeamStats?.eps_poss || 0 },
                            ]}
                            >
                            <PolarGrid />
                            <PolarAngleAxis dataKey="name" />
                            <PolarRadiusAxis tickFormatter={(value) => `${value.toFixed(2)}`} />
                            <Radar
                                name="Metrics Per Possession"
                                dataKey="value"
                                stroke={COLORS[3]}
                                fill={COLORS[3]}
                                fillOpacity={0.6}
                            />
                            <Tooltip formatter={(value) => `${value.toFixed(2)}`} />
                            </RadarChart>
                        </Card>
                        </Col>
                    </Row>
                    </TabPane>

            {/* Shooting Stats */}
            <TabPane tab="Shooting Stats" key="4">
              <Row gutter={[16, 16]} justify="center">
                <Col span={12}>
                <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Shots Attempted & Made
                        </Typography.Text>
                    }
                    style={{
                        backgroundColor: '#f5f5f5', // Light grey background>
                    }}>

                    <BarChart
                      width={500}
                      height={300}
                      data={[
                        { name: 'FGA', value: derivedTeamStats?.fga || 0 },
                        { name: 'FGM', value: derivedTeamStats?.fgm || 0 },
                        { name: '3PA', value: derivedTeamStats?.three_pa || 0 },
                        { name: '3PM', value: derivedTeamStats?.three_pm || 0 },
                        { name: 'FTA', value: derivedTeamStats?.fta || 0 },
                        { name: 'FTM', value: derivedTeamStats?.ftm || 0 },
                      ]}
                    >
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => `${(value).toFixed(0)}`} />
                      <Bar dataKey="value">
                        {COLORS.map((color, idx) => (
                          <Cell key={idx} fill={color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </Card>
                </Col>
                <Col span={12}>
                <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Shooting Percentage
                        </Typography.Text>
                    }>
                    <RadarChart
                      outerRadius={130}
                      width={500}
                      height={300}
                      data={[
                        { name: 'eFG%', value: derivedTeamStats?.efg_pct || 0 },
                        { name: 'FG%', value: derivedTeamStats?.team_fg_pct || 0 },
                        { name: '3P%', value: derivedTeamStats?.team_three_pct || 0 },
                        { name: 'FT%', value: derivedTeamStats?.team_ft_pct || 0 },
                        { name: 'TS%', value: derivedTeamStats?.ts_pct || 0 },
                      ]}
                    >
                      <PolarGrid />
                      <PolarAngleAxis dataKey="name" />
                      <PolarRadiusAxis />
                      <Radar
                        dataKey="value"
                        stroke={COLORS[0]}
                        fill={COLORS[0]}
                        fillOpacity={0.6}
                      />
                      <Tooltip formatter={(value) => `${(value * 100).toFixed(2)}%`} />
                    </RadarChart>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="Defensive Impact and Playmaking" key="5">
                {/* Row containing both charts */}
                <Row gutter={[16, 16]} justify="center" style={{ marginBottom: '20px' }}>
                    {/* Bar Chart for Defensive Impact */}
                    <Col span={12}>
                    <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Defensive Impact
                        </Typography.Text>
                    }
                    style={{
                        backgroundColor: '#f5f5f5', // Light grey background>
                    }}>
                        <BarChart
                        width={500}
                        height={300}
                        data={[
                            { name: 'STL', value: derivedTeamStats?.stl || 0 },
                            { name: 'BLK', value: derivedTeamStats?.blk || 0 },
                        ]}
                        >
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(value) => `${value.toFixed(0)}`} />
                        <Tooltip formatter={(value) => `${value.toFixed(0)}`} />
                        <Bar dataKey="value">
                            <Cell fill={COLORS[0]} />
                            <Cell fill={COLORS[3]} />
                        </Bar>
                        </BarChart>
                    </Card>
                    </Col>

                    {/* Donut Chart for AST/TOV */}
                    <Col span={12}>
                    <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Playmaking
                        </Typography.Text>
                    }>
                        <PieChart width={500} height={300}>
                        <Pie
                            data={[
                            { name: 'AST', value: derivedTeamStats?.ast || 0 },
                            { name: 'TOV', value: derivedTeamStats?.tov || 0 },
                            ]}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            innerRadius={80}
                            outerRadius={140}
                            fill="#82ca9d"
                            label={false} // Hide individual slice labels
                        >
                            <Cell fill={COLORS[0]} />
                            <Cell fill={COLORS[3]} />
                        </Pie>
                        <Tooltip formatter={(value) => value} />
                        {/* AST/TOV Ratio Text in the Center */}
                        <text
                            x="50%"
                            y="50%"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={18}
                            fontWeight="bold"
                        >
                            {`AST/TO: ${derivedTeamStats?.ast_to?.toFixed(2) || 'N/A'}`}
                        </text>
                        </PieChart>
                    </Card>
                    </Col>
                </Row>
                </TabPane>


            <TabPane tab="Rebounding" key="6">
            <Row gutter={[16, 16]} justify="center" style={{ marginBottom: '20px' }}>
                {/* Donut Chart for Rebounds */}
                <Col span={12}>
                <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Rebound Distribution
                        </Typography.Text>
                    }>
                    <PieChart width={500} height={300}>
                    <Pie
                        data={[
                        { name: 'OREB', value: derivedTeamStats?.oreb || 0 },
                        { name: 'DREB', value: derivedTeamStats?.dreb || 0 },
                        ]}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={140}
                        fill="#82ca9d"
                        label={false} // Hide individual slice labels
                    >
                        <Cell fill={COLORS[0]} />
                        <Cell fill={COLORS[3]} />
                    </Pie>
                    <Tooltip formatter={(value) => value} />
                    {/* Total Rebounds Text in the Center */}
                    <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={18}
                        fontWeight="bold"
                    >
                        {`REB: ${derivedTeamStats?.reb || 'N/A'}`}
                    </text>
                    </PieChart>
                </Card>
                </Col>

                {/* Bar Chart for OREB% and DREB% */}
                <Col span={12}>
                <Card title={
                        <Typography.Text strong style={{ fontSize: '1.2rem', color: '#465892' }}>
                            Rebounds Percentage
                        </Typography.Text>
                    }
                    style={{
                        backgroundColor: '#f5f5f5', // Light grey background>
                    }}>
                    <BarChart
                    width={500}
                    height={300}
                    data={[
                        { name: 'OREB%', value: analyticalTeamStats?.oreb_pct || 0 },
                        { name: 'DREB%', value: analyticalTeamStats?.dreb_pct || 0 },
                    ]}
                    >
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `${(value * 100).toFixed(0)}%`} />
                    <Tooltip formatter={(value) => `${(value * 100).toFixed(2)}%`} />
                    <Bar dataKey="value">
                        <Cell fill={COLORS[0]} />
                        <Cell fill={COLORS[3]} />
                    </Bar>
                    </BarChart>
                </Card>
                </Col>
            </Row>
            </TabPane>

          </Tabs>
        </>
      )}
    </div>
  );
};

export default TeamStats;
